import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { formatErrorDetail } from "@/lib/format-error";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  RefreshCw,
  Save,
  ListFilter,
  Mail,
  ChevronRight,
  ArrowLeft,
  Workflow,
  Zap,
  KeyRound,
} from "lucide-react";

// ---------- HubSpot list-whitelist editor ----------

const HubspotListsSetting = ({ authHeaders, settings, onSettingsChanged }) => {
  const [hubspotLists, setHubspotLists] = useState([]);
  const [allowedIds, setAllowedIds] = useState(new Set(settings?.allowed_hubspot_list_ids || []));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/hubspot/lists`, { withCredentials: true, headers: authHeaders });
        if (!cancelled) setHubspotLists(res.data.lists || []);
      } catch (err) {
        toast.error(formatErrorDetail(err, "Failed to load HubSpot lists"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const toggleListId = (listId) => {
    setAllowedIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) next.delete(listId); else next.add(listId);
      return next;
    });
  };
  const selectAll = () => setAllowedIds(new Set(hubspotLists.map((l) => l.list_id)));
  const clearAll = () => setAllowedIds(new Set());

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/settings`,
        { allowed_hubspot_list_ids: Array.from(allowedIds) },
        { withCredentials: true, headers: authHeaders },
      );
      onSettingsChanged(res.data);
      toast.success("HubSpot lists saved");
    } catch (err) {
      toast.error(formatErrorDetail(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ListFilter className="w-5 h-5 text-indigo-600" /> HubSpot list whitelist
        </CardTitle>
        <CardDescription>
          Tick the lists that should appear in the dropdowns across Email Tools and Sequences. Leave everything unticked to show all lists (default).
          {" "}<strong className="text-slate-700">{allowedIds.size} of {hubspotLists.length} ticked.</strong>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : hubspotLists.length === 0 ? (
          <p className="text-slate-500 text-sm">No HubSpot lists found.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={selectAll}>Select all</Button>
              <Button variant="outline" size="sm" onClick={clearAll}>Clear (= show all)</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {hubspotLists.map((l) => {
                const checked = allowedIds.has(l.list_id);
                return (
                  <label
                    key={l.list_id}
                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                      checked ? "border-indigo-200 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleListId(l.list_id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{l.name}</div>
                      <div className="text-xs text-slate-500">
                        ID {l.list_id}
                        {l.processing_type && <Badge variant="outline" className="ml-2 text-xs">{l.processing_type}</Badge>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="mt-6 flex items-center gap-2">
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ---------- Test-send recipient editor ----------

const TestSendRecipientSetting = ({ authHeaders, settings, currentUserEmail, onSettingsChanged }) => {
  const [email, setEmail] = useState(settings?.test_send_recipient_email || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const cleaned = email.trim().toLowerCase();
    if (cleaned && !cleaned.includes("@")) {
      toast.error("Enter a valid email or leave blank to clear");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/settings`,
        { test_send_recipient_email: cleaned },
        { withCredentials: true, headers: authHeaders },
      );
      onSettingsChanged(res.data);
      toast.success(cleaned ? `Test sends will go to ${cleaned}` : "Cleared — test sends default to your account email");
    } catch (err) {
      toast.error(formatErrorDetail(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-indigo-600" /> Test-send recipient
        </CardTitle>
        <CardDescription>
          Default email address for "Send a test" buttons (in the template editor and elsewhere). Leave blank to default to your own account email ({currentUserEmail || "—"}).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 max-w-md">
          <Label htmlFor="test-recipient">Default test recipient</Label>
          <Input
            id="test-recipient"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={currentUserEmail || "you@example.com"}
            disabled={saving}
          />
          <p className="text-xs text-slate-500">
            {settings?.test_send_recipient_email
              ? <>Currently set to <span className="font-mono">{settings.test_send_recipient_email}</span>. Editors will pre-fill this.</>
              : <>Currently unset — editors pre-fill the logged-in user's email.</>
            }
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------- HubSpot timeline logging toggle ----------

const HubspotTimelineLoggingSetting = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Workflow className="w-5 h-5 text-slate-400" /> HubSpot timeline logging
        </CardTitle>
        <CardDescription>
          When on, every email Storm Pad sends to a HubSpot contact (and every delivery / open / click / bounce that follows) would be logged to that contact's HubSpot activity timeline as a Storm Pad event.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This feature is currently disabled. HubSpot's Timeline API rejects Private App tokens, and the Public-App route requires HubSpot's app-events approval. The setting is kept here so it can be revived later if the integration model changes.
        </div>

        <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 bg-slate-50 cursor-not-allowed opacity-60">
          <input
            type="checkbox"
            checked={false}
            disabled
            className="mt-1 w-4 h-4"
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-700">
              Log Storm Pad email activity to HubSpot timelines
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Disabled. To track engagement, use the <strong>Sent Items</strong> page or the open / click icons in the Recent Sends table.
            </div>
          </div>
        </label>
      </CardContent>
    </Card>
  );
};

// ---------- Default auto-sync interval ----------

const AUTO_SYNC_INTERVAL_OPTIONS = [
  { value: 0, label: "Off" },
  { value: 1, label: "Every 1 minute" },
  { value: 5, label: "Every 5 minutes" },
  { value: 10, label: "Every 10 minutes" },
  { value: 15, label: "Every 15 minutes" },
  { value: 30, label: "Every 30 minutes" },
  { value: 60, label: "Every 60 minutes" },
];

const DefaultAutoSyncSetting = ({ authHeaders, settings, onSettingsChanged }) => {
  const [value, setValue] = useState(settings?.default_auto_sync_minutes ?? 0);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/settings`,
        { default_auto_sync_minutes: Number(value) },
        { withCredentials: true, headers: authHeaders },
      );
      onSettingsChanged(res.data);
      toast.success("Default auto-sync interval saved");
    } catch (err) {
      toast.error(formatErrorDetail(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-indigo-600" /> Default auto-sync interval
        </CardTitle>
        <CardDescription>
          How often Storm Pad re-checks each sequence's HubSpot list for new contacts to enrol or contacts to cancel. Each sequence can override this in its own Setup tab — this is just the fallback for sequences set to "Use default".
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 max-w-md">
          <Label className="text-sm">Default interval</Label>
          <Select value={String(value)} onValueChange={(v) => setValue(Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {AUTO_SYNC_INTERVAL_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            Tip: 1-minute is best for testing only. For normal use, 10–15 minutes is plenty — list changes rarely need sub-10-minute reactions.
          </p>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------- Settings index (drill-in list) ----------

const SETTINGS_ITEMS = [
  {
    key: "hubspot-lists",
    label: "HubSpot list whitelist",
    description: "Choose which HubSpot lists appear in dropdowns across the app.",
    icon: ListFilter,
    summary: (s) => {
      const n = (s?.allowed_hubspot_list_ids || []).length;
      return n === 0 ? "Showing all lists" : `${n} list${n === 1 ? "" : "s"} whitelisted`;
    },
  },
  {
    key: "test-send-recipient",
    label: "Test-send recipient",
    description: "Default email address for test-send buttons.",
    icon: Mail,
    summary: (s, userEmail) => s?.test_send_recipient_email
      ? s.test_send_recipient_email
      : `Defaults to ${userEmail || "logged-in user"}`,
  },
  {
    key: "hubspot-timeline-logging",
    label: "HubSpot timeline logging",
    description: "Mirror Storm Pad email activity to HubSpot contact timelines.",
    icon: Workflow,
    summary: () => "Disabled",
  },
  {
    key: "default-auto-sync",
    label: "Default auto-sync interval",
    description: "Default polling interval for sequences without their own setting.",
    icon: Zap,
    summary: (s) => {
      const m = s?.default_auto_sync_minutes ?? 0;
      if (!m) return "Off";
      return m === 1 ? "Every 1 min" : `Every ${m} min`;
    },
  },
  // Items with `route` navigate to a separate page rather than rendering
  // inline below. Used for things that already have their own full-page UI.
  {
    key: "api-keys",
    label: "MCP API Keys",
    description: "Bearer-token credentials for dev tools and scripts (Claude.ai uses OAuth).",
    icon: KeyRound,
    route: "/api-keys",
    summary: () => "Manage keys for MCP Inspector, cron jobs, integrations",
  },
];

const Settings = ({ user, setUser }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings`, { withCredentials: true, headers: authHeaders });
      setSettings(res.data);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load settings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); /* eslint-disable-next-line */ }, []);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    localStorage.removeItem("token"); navigate("/");
  };

  const activeItem = SETTINGS_ITEMS.find((i) => i.key === activeKey);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="settings-page">
      <Sidebar user={user} onLogout={handleLogout} />
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            {activeItem && (
              <Button variant="ghost" size="sm" onClick={() => setActiveKey(null)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                {activeItem ? activeItem.label : "Settings"}
              </h1>
              <p className="text-slate-500 mt-1">
                {activeItem ? activeItem.description : "App-wide configuration for your organisation."}
              </p>
            </div>
          </div>
          {!activeItem && (
            <Button variant="ghost" size="sm" onClick={fetchSettings} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : activeKey === "hubspot-lists" ? (
          <HubspotListsSetting
            authHeaders={authHeaders}
            settings={settings}
            onSettingsChanged={setSettings}
          />
        ) : activeKey === "test-send-recipient" ? (
          <TestSendRecipientSetting
            authHeaders={authHeaders}
            settings={settings}
            currentUserEmail={user?.email || ""}
            onSettingsChanged={setSettings}
          />
        ) : activeKey === "hubspot-timeline-logging" ? (
          <HubspotTimelineLoggingSetting
            authHeaders={authHeaders}
            settings={settings}
            onSettingsChanged={setSettings}
          />
        ) : activeKey === "default-auto-sync" ? (
          <DefaultAutoSyncSetting
            authHeaders={authHeaders}
            settings={settings}
            onSettingsChanged={setSettings}
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <ul className="divide-y divide-slate-100">
                {SETTINGS_ITEMS.map((item) => {
                  const Icon = item.icon;
                  // Items with a `route` jump to a separate page (e.g. API Keys);
                  // others drill in inline by setting activeKey.
                  const onClick = item.route
                    ? () => navigate(item.route)
                    : () => setActiveKey(item.key);
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        onClick={onClick}
                        className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900">{item.label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{item.description}</div>
                          <div className="text-xs text-slate-600 mt-1">{item.summary(settings, user?.email)}</div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Settings;
